import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, Loader2, CheckCircle, XCircle, Replace, Search
} from "lucide-react";
import { LOGO_URL } from "../config/constants";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

function getBonsaiUserId() {
  // Canonical session credential — set by LoginPage on email/password login
  // and by the MS-SSO callback. session_guard.require_session looks this up
  // as ``id`` in db.users / db.bonsai_users, so it MUST be the stored id
  // (email for legacy logins, UUID for SSO) — never derived elsewhere.
  return (
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    ""
  );
}

function getUserEmail() {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const u = JSON.parse(userStr);
      if (u.email) return u.email;
    }
  } catch (e) {}
  const email =
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    "";
  if (email) return email;
  const uid = getBonsaiUserId();
  return uid.includes("@") ? uid : "";
}

function getHeaders() {
  const userId = getBonsaiUserId();
  const email = getUserEmail();
  return {
    "X-Bonsai-User-Id": userId,
    "X-User-Email": email,
    "Content-Type": "application/json",
  };
}

// Shape a fetch error so utils/errorMessage can read the server payload.
// Without this, a fetch !resp.ok path throws a bare Error whose message is
// the raw detail string — but errorMessage's priority chain looks at
// err.response.data first, so bare Errors fall through to the "Couldn't
// reach the server" branch. That masked the real backend error for the
// operator when the Ref Fixer preview / confirm hit a domain-level 400/409
// (e.g. "Target matter reference already exists"). Wrap the fetch failure
// as an axios-shaped rejection so all messaging flows through the same
// canonical formatter.
async function buildFetchError(resp) {
  let data = null;
  const text = await resp.text().catch(() => "");
  if (text) {
    try { data = JSON.parse(text); } catch (e) { data = text; }
  }
  const err = new Error(
    (data && typeof data === "object" && (data.detail?.message || data.detail || data.message || data.error))
    || (typeof data === "string" && data)
    || `${resp.status} ${resp.statusText}`
  );
  err.response = {
    status: resp.status,
    statusText: resp.statusText,
    data: data,
  };
  return err;
}

/* ── Per-matter correction row ── */
function MatterRefRow({ matter, rowIndex, onCorrected }) {
  const [newRef, setNewRef] = useState("");
  // WP-J two-stage UX: idle → previewing → preview_ready → confirming → success
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const [preview, setPreview] = useState(null);

  // Bulk rename (WP-J). Used unless the preview reports collision=true.
  const callCorrect = async (dryRun) => {
    const resp = await fetch(`${API}/admin/correct-matter-reference`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        old_reference: matter.matter_reference,
        new_reference: newRef.trim(),
        dry_run: dryRun,
        record_id: matter.record_id || undefined,
        collection: matter.collection || undefined,
        submission_id: matter.submission_id || undefined,
        reason: "Manual correction via Ref Fixer",
      }),
    });
    if (!resp.ok) {
      throw await buildFetchError(resp);
    }
    return resp.json();
  };

  // LV-17b scoped rename. Used when the WP-J preview reports
  // collision=true — the identity scopes the rename to THIS matter
  // row's client so the sibling client's rows are left untouched.
  // The row already carries client_name from the search result, so the
  // operator doesn't have to re-enter anything.
  const callCorrectScoped = async (dryRun) => {
    const resp = await fetch(`${API}/admin/correct-matter-reference-scoped`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        old_reference: matter.matter_reference,
        new_reference: newRef.trim(),
        identity: {
          client_name: matter.client_name || "",
          // matter_type + intake_at left empty deliberately — the
          // scoped matcher treats an empty query field as "match any"
          // (see matter_ref_scoped.IdentityKey.matches). Client name
          // alone is enough to disambiguate NE from Sharon.
          matter_type: "",
          intake_at: "",
        },
        dry_run: dryRun,
        reason: "Manual correction via Ref Fixer (scoped — collision)",
      }),
    });
    if (!resp.ok) {
      throw await buildFetchError(resp);
    }
    return resp.json();
  };

  // Track which endpoint the current preview came from so Confirm
  // routes to the matching write call.
  const [scopedMode, setScopedMode] = useState(false);

  const previewChange = async () => {
    if (!newRef.trim()) {
      setMsg("Enter a new reference");
      setState("error");
      return;
    }
    setState("previewing");
    setMsg("");
    setPreview(null);
    setScopedMode(false);
    try {
      // First try WP-J bulk preview.
      const data = await callCorrect(true);
      const planned = data.result?.planned || null;
      const hasCollision = planned?.collision === true;

      if (hasCollision) {
        // Collision — reissue the preview against the LV-17b scoped
        // endpoint scoped to THIS matter row's client. The scoped
        // preview returns a different shape (planned is a list, no
        // top-level collision key, skipped_counts per collection).
        // Normalise it into the same shape the JSX renders so the
        // existing preview block just works.
        const scopedData = await callCorrectScoped(true);
        const scopedPlanned = scopedData.result?.planned || [];
        const scopedSharepoint = scopedData.result?.sharepoint_rename || null;
        const totalSkipped = scopedPlanned.reduce(
          (n, c) => n + (c.skip_count || 0), 0);
        setPreview({
          collections: scopedPlanned,
          sharepoint_rename: scopedSharepoint,
          collision: false,        // scoped is the disambiguation path
          scoped: true,
          scoped_skipped: totalSkipped,
          scoped_identity: scopedData.result?.identity || null,
        });
        setScopedMode(true);
        setState("preview_ready");
        setMsg(
          `Collision resolved: scoped to ${matter.client_name || "this client"} ` +
          `(${totalSkipped} sibling row${totalSkipped === 1 ? "" : "s"} left untouched).`
        );
      } else {
        setPreview(planned);
        setState("preview_ready");
        setMsg("Preview ready — review then confirm.");
      }
    } catch (err) {
      setState("error");
      setMsg(errorMessage(err, "Preview failed"));
    }
  };

  const confirmChange = async () => {
    setState("confirming");
    setMsg("");
    try {
      // Confirm against whichever endpoint the preview came from.
      const data = scopedMode ? await callCorrectScoped(false)
                              : await callCorrect(false);
      setState("success");
      const suffix = scopedMode ? " (scoped)" : "";
      setMsg(`Updated ${data.total_updated} doc(s)${suffix}`);
      // WP-J: tell pipeline list its cache is stale so the next render refetches.
      try { sessionStorage.setItem("bonsai_pipeline_cache_invalidated_at", String(Date.now())); } catch (e) {}
      onCorrected && onCorrected(matter.submission_id || `ref-row-${rowIndex}`);
    } catch (err) {
      setState("error");
      setMsg(errorMessage(err, "Confirm failed"));
    }
  };

  const cancelPreview = () => {
    setPreview(null);
    setState("idle");
    setMsg("");
    setScopedMode(false);
  };

  const done = state === "success";
  const busy = state === "previewing" || state === "confirming";
  const collision = preview?.collision === true;
  const scoped = preview?.scoped === true;

  return (
    <div
      className={`flex flex-col gap-2 py-3 px-3 rounded-md border transition-all ${
        done
          ? "border-teal/40 bg-teal-dim/20 opacity-60"
          : "border-navy-border bg-navy-card"
      }`}
      data-testid={`ref-row-${rowIndex}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-semibold text-txt leading-tight">
            {matter.client_name || "Unknown client"}
          </p>
          <p className="text-xs text-txt-muted mt-0.5">
            {matter.collection && <span className="mr-2 text-txt-faint">[{matter.collection}]</span>}
            {matter.form_type && <span className="mr-2">{matter.form_type}</span>}
            {matter.fee_earner && <span className="mr-2">· {matter.fee_earner}</span>}
            {matter.created_at && <span>· opened {matter.created_at}</span>}
          </p>
          <p className="text-xs font-mono text-status-amber mt-1">{matter.matter_reference}</p>
        </div>

        <Input
          placeholder="New reference"
          value={newRef}
          onChange={(e) => setNewRef(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && state === "idle" && previewChange()}
          disabled={done || busy || state === "preview_ready"}
          className="w-44 bg-navy border-navy-border text-txt placeholder:text-txt-faint font-mono text-sm"
          data-testid={`ref-row-${rowIndex}-new-ref-input`}
        />

        {state !== "preview_ready" && state !== "confirming" && (
          <Button
            onClick={previewChange}
            disabled={done || busy || !newRef.trim()}
            className="gap-1.5 bg-status-amber/80 hover:bg-status-amber border border-amber-600/40 text-white text-sm disabled:opacity-40"
            data-testid={`ref-row-${rowIndex}-preview-btn`}
          >
            {state === "previewing" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Search className="w-3.5 h-3.5" />
            )}
            Preview
          </Button>
        )}

        {(state === "preview_ready" || state === "confirming") && (
          <>
            <Button
              onClick={confirmChange}
              disabled={busy || collision}
              title={collision ? "Collision detected — cannot confirm without disambiguation" : ""}
              className="gap-1.5 bg-teal hover:bg-teal/90 border border-teal text-white text-sm disabled:opacity-40"
              data-testid={`ref-row-${rowIndex}-confirm-btn${scoped ? "-scoped" : ""}`}
            >
              {state === "confirming" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Replace className="w-3.5 h-3.5" />
              )}
              Confirm
            </Button>
            <Button
              onClick={cancelPreview}
              disabled={busy}
              variant="outline"
              className="gap-1.5 border-navy-border text-txt-muted text-sm"
              data-testid={`ref-row-${rowIndex}-cancel-btn`}
            >
              Cancel
            </Button>
          </>
        )}

        {state === "success" && (
          <span className="text-xs text-teal flex items-center gap-1" data-testid={`ref-row-${rowIndex}-success`}>
            <CheckCircle className="w-3.5 h-3.5" />
            {msg}
          </span>
        )}
        {state === "error" && (
          <span className="text-xs text-status-red flex items-center gap-1" data-testid={`ref-row-${rowIndex}-error`}>
            <XCircle className="w-3.5 h-3.5" />
            {msg}
          </span>
        )}
      </div>

      {preview && state === "preview_ready" && (
        <div
          className="mt-1 rounded-md border border-navy-border/60 bg-navy p-3 text-xs text-txt-muted"
          data-testid={`ref-row-${rowIndex}-preview`}
        >
          <p className="font-semibold text-txt mb-1">
            Planned changes: {matter.matter_reference} → {newRef.trim()}
          </p>
          {scoped && (
            <p className="text-status-amber mb-2" data-testid={`ref-row-${rowIndex}-scoped-banner`}>
              Scoped rename: this matter reference is shared by another client.
              Confirming will rename only rows belonging to
              <span className="font-mono"> {matter.client_name || "this client"}</span>.
              {typeof preview.scoped_skipped === "number" && preview.scoped_skipped > 0 && (
                <> {preview.scoped_skipped} sibling row{preview.scoped_skipped === 1 ? "" : "s"} will be left untouched.</>
              )}
            </p>
          )}
          {collision && (
            <p className="text-status-red mb-2">
              Collision: more than one record shares the old reference in a
              collection. Disambiguate before confirming.
            </p>
          )}
          {preview.collections?.length ? (
            <ul className="space-y-0.5">
              {preview.collections.map((c) => (
                <li key={c.name} className="font-mono">
                  · {c.name}: {c.match_count} record{c.match_count === 1 ? "" : "s"}
                  {typeof c.skip_count === "number" && c.skip_count > 0 && (
                    <span className="text-txt-faint"> · {c.skip_count} skipped</span>
                  )}
                  {c.fields?.length > 1 ? ` (+${c.fields.length - 1} nested)` : ""}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-txt-muted">No matter-ref-keyed records matched — confirm will be a no-op.</p>
          )}
          {preview.sharepoint_rename ? (
            <p className="mt-2">
              SharePoint: folder will be renamed to <span className="font-mono">{preview.sharepoint_rename.new_name}</span>
            </p>
          ) : (
            <p className="mt-2 text-txt-faint italic">SharePoint folder rename will be skipped (no drive_id on record).</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function RefFixerPage() {
  const navigate = useNavigate();
  const [searchRef, setSearchRef] = useState("");
  const [lookupState, setLookupState] = useState("idle");
  const [matters, setMatters] = useState([]);
  const [lookupMsg, setLookupMsg] = useState("");
  const [corrected, setCorrected] = useState(new Set());

  const lookup = async () => {
    if (!searchRef.trim()) return;
    setLookupState("loading");
    setMatters([]);
    setLookupMsg("");
    setCorrected(new Set());
    try {
      const resp = await fetch(
        `${API}/admin/search-matters?q=${encodeURIComponent(searchRef.trim())}`,
        { method: "GET", headers: getHeaders() }
      );
      if (!resp.ok) {
        throw await buildFetchError(resp);
      }
      const data = await resp.json();
      const list = data.matches || [];
      setMatters(list);
      setLookupState("done");
      setLookupMsg(
        list.length === 0
          ? "No matters found."
          : `Found ${list.length} matter${list.length !== 1 ? "s" : ""}`
      );
    } catch (err) {
      setLookupState("error");
      setLookupMsg(errorMessage(err, "Lookup failed"));
    }
  };

  const markCorrected = (id) =>
    setCorrected((prev) => new Set([...prev, id]));

  const allDone =
    matters.length > 0 && corrected.size === matters.length;

  return (
    <div className="min-h-screen bg-navy">
      {/* Top bar */}
      <nav className="glass-nav sticky top-0 z-50 w-full">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Bonsai Law" className="w-9 h-9" />
              <div>
                <h1 className="text-base font-bold text-teal leading-tight">
                  Reference Fixer
                </h1>
                <p className="text-[10px] text-txt-muted tracking-wide font-medium leading-tight">
                  Matter reference correction
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-txt-muted border-navy-border/40 hover:border-teal/50 hover:text-teal gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <section className="glass-card p-6 rounded-md space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-teal uppercase tracking-wider flex items-center gap-2 mb-1">
              <Replace className="w-4 h-4" /> Matter Reference Correction
            </h2>
            <p className="text-xs text-txt-muted">
              Search by current matter reference or client name, then enter
              the corrected reference for each result.
            </p>
          </div>

          {/* Step 1 — look up */}
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Search by reference or client name"
              value={searchRef}
              onChange={(e) => setSearchRef(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              className="w-72 bg-navy-card border-navy-border text-txt placeholder:text-txt-muted font-mono text-sm"
            />
            <Button
              onClick={lookup}
              disabled={lookupState === "loading" || !searchRef.trim()}
              className="gap-1.5 bg-navy-hover border border-navy-border text-txt hover:bg-teal hover:text-white disabled:opacity-40"
            >
              {lookupState === "loading" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Look up
            </Button>
            {lookupMsg && (
              <span
                className={`text-xs flex items-center gap-1 ${
                  lookupState === "error"
                    ? "text-status-red"
                    : matters.length === 0
                    ? "text-txt-muted"
                    : "text-txt-muted"
                }`}
              >
                {lookupState === "error" && <XCircle className="w-3.5 h-3.5" />}
                {lookupMsg}
              </span>
            )}
          </div>

          {/* Step 2 — per-matter rows */}
          {matters.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-txt-muted">
                Enter the correct reference for each matter individually, then
                click Correct.
              </p>
              {matters.map((m, i) => (
                <MatterRefRow
                  key={`${m.submission_id || 'no-id'}-${m.matter_reference || 'no-ref'}-${i}`}
                  matter={m}
                  rowIndex={i}
                  onCorrected={markCorrected}
                />
              ))}
              {allDone && (
                <p className="text-xs text-teal flex items-center gap-1 pt-1">
                  <CheckCircle className="w-3.5 h-3.5" /> All matters corrected.
                </p>
              )}
            </div>
          )}
        </section>

        <p className="text-xs text-txt-muted text-center mt-6">
          Logged in as {getUserEmail()}
        </p>
      </main>
    </div>
  );
}
